import React, { useEffect, useState } from 'react';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { ButcherHeader } from '@components/ButcherHeader';
import {
  errorMessage,
  ManagementSubsteps,
  onboardingStepId,
  responseTexts,
} from '@helpers/Constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faPen,
  faInfoCircle,
  faSearch,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { AddRoleModal } from '@components/Modal/RoleManagement/AddRole';
import { Axios } from '@helpers/ApiService';
import EditRoleModal from '@components/Modal/RoleManagement/EditRole';
import { useToast } from '@contexts/Toast';
import { RoleList } from '@helpers/Models';
import {
  ApiEndpoints,
  FrontendRoutes,
  onboardingSteps,
} from '@helpers/Constants/Navigation';
import { useLocation, useNavigate } from 'react-router-dom';
import { OnBoardSettings } from '@components/OnBoardSettings';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { UseConfirmationToast } from '@contexts/ConfirmationToast';

export const RoleManagement: React.FC = () => {
  const { updateOnboardingStep } = useLocalStorage();
  const [isAddingNewRole, setIsAddingNewRole] = useState(false);
  const [isEditingRole, setIsEditingRole] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [roles, setRoles] = useState<RoleList[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const { showToastMessage } = useToast();
  const { showConfirmationToast } = UseConfirmationToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [showTooltip, setShowTooltip] = useState(false);
  const [lastOperation, setLastOperation] = useState<'create' | 'edit' | null>(null);

  const fromSettings = location.state?.fromSettings;

  useEffect(() => {
    fetchModules(false);
  }, []);

  const fetchModules = async (showSuccessToast = false): Promise<void> => {
    try {
      const moduleResponse = await Axios.post(ApiEndpoints.FETCH_ROLES, {});
      setRoles(moduleResponse.data.data);
      if (showSuccessToast && moduleResponse.data.data.length > 0) {
        showToastMessage(responseTexts.ROLE_FETCH_SUCCESS, 'success');
      }
    } catch (error) {
      showToastMessage(errorMessage.ERROR_FETCHING_ROLE, 'error');
    }
  };

  const performDelete = async (roleId: string): Promise<void> => {
    try {
      await Axios.delete(`${ApiEndpoints.DELETE_ROLE}`, {
        data: { id: roleId },
      });
      showToastMessage(responseTexts.ROLE_DELETION_SUCCESS, 'success');
      fetchModules();
    } catch (error) {
      showToastMessage(errorMessage.ERROR_DELETING_ROLE, 'error');
    }
  };

  const deleteRole = (roleId: string): void => {
    const role = roles.find((r) => r.id === roleId);
    if (role && !role.isDeletable) {
      showToastMessage(responseTexts.ROLE_DELETION_RESTRICTED, 'error');
      return;
    }

    showConfirmationToast(
      'Are you sure you want to delete this role?',
      'Delete Confirmation',
      'delete',
      () => performDelete(roleId),
      'Delete',
      undefined,
      'Cancel'
    );
  };

  const handleModalClose = () => {
    // After the modal closes, refetch and show the appropriate toast
    if (lastOperation === 'create') {
      showToastMessage('Role created successfully.', 'success');
    } else if (lastOperation === 'edit') {
      showToastMessage('Role updated successfully.', 'success');
    }
    setLastOperation(null);
    fetchModules();
  };

  const handleSubmit: (
    event: React.FormEvent,
    nextPage: boolean,
  ) => Promise<void> = async (event, nextPage) => {
    event.preventDefault();

    updateOnboardingStep(onboardingStepId.ROLE_MANAGEMENT);
    await Axios.post(ApiEndpoints.UPDATE_ONBOARDING_STEP, {
      stepName: onboardingSteps.ROLE_MANAGEMENT,
    });

    if (nextPage) {
      if (fromSettings) {
        navigate(FrontendRoutes.USER_MANAGEMENT, { state: { fromSettings } });
      } else {
        navigate(FrontendRoutes.USER_MANAGEMENT);
      }
    }
  };

  const filteredRoles = roles.filter((role) =>
    role.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const editRole = (roleId: string): void => {
    setSelectedRoleId(roleId);
    setIsEditingRole(true);
    setLastOperation('edit');
  };

  const renderContent = (
    <>
      <div>
        <Breadcrumbs
          title="Configure Settings"
          activeMainNav="Roles and users"
          activeStep={1}
          subNavSteps={ManagementSubsteps}
          fromSettings={fromSettings ? true : false}
          forStoreSetting={fromSettings ? true : false}
        />
      </div>
      <hr className="my-4 border-t border-gray-300" />

      <div id="search-header" className="mb-4">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2 w-1/2">
            <h2 className="text-xl font-bold leading-none">Role Management</h2>
            <div
              className="relative inline-flex items-center justify-center h-5 w-5"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="text-gray-400 hover:text-gray-600 cursor-pointer text-base transition-colors duration-200"
              />
              {showTooltip && (
                <div className="absolute left-0 top-full mt-0 w-72 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl z-50 leading-relaxed">
                  <div className="absolute bottom-full left-3 border-8 border-transparent border-b-gray-900" />
                  <p className="font-normal normal-case tracking-normal">
                    <strong className="block mb-1 text-gray-200 text-[13px] font-semibold">
                      Role Management
                    </strong>
                    Create and manage user roles and system permissions for your butcher house. Assigned roles determine page access and restrict unauthorized actions across the application.
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="relative w-1/2 flex items-center justify-end">
            <div className="flex w-96 px-4 py-1 justify-between items-center rounded border border-[#C9C5C6] bg-white shadow-sm">
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pr-4 py-1 outline-none border-none focus:ring-0 text-sm"
              />
              <FontAwesomeIcon icon={faSearch} className="ml-3 text-gray-400" />
            </div>
          </div>
        </div>

        {roles.length === 0 ? (
          <div className="flex justify-center items-center h-64">
            <p className="text-gray-500 mt-8">No data available</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-2 gap-4 w-full">
            {filteredRoles.map((role) => (
              <div
                key={role.id}
                className="role-card flex bg-white shadow-md hover:shadow-lg transition-shadow duration-200 rounded-lg border border-gray-200 p-4 w-full"
              >
                <div className="role-header p-2 w-full justify-between items-center">
                  <div className="flex w-full justify-between items-center">
                    <div className="role-name">
                      <h2 className="text-xl font-bold">{role.name}</h2>
                    </div>
                    <div className="role-actions">
                      <button
                        type="button"
                        onClick={() => editRole(role.id)}
                        className="text-primary font-bold"
                      >
                        <FontAwesomeIcon icon={faPen} /> Edit
                      </button>
                      {role.isDefault === 0 && (
                        <span>
                          <FontAwesomeIcon
                            icon={faTrash}
                            onClick={() => deleteRole(role.id)}
                            className="ml-3 text-gray-500 cursor-pointer"
                          />
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="pt-4">
                    <div className="role-desc">
                      <p className="text-gray-500 font-bold">Description</p>
                      <p className="desc-exp pb-2 border-b-gray-300 border-b-2">
                        {role.desc}
                      </p>
                    </div>
                  </div>

                  <div className="pt-4">
                    <div className="role-desc">
                      <p className="text-gray-500 text-lg font-bold">
                        Permissions
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      {role.permissions.map((permission, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <span className="mt-4">{permission.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <AddRoleModal
          isOpen={isAddingNewRole}
          onClose={() => {
            setIsAddingNewRole(false);
            if (lastOperation === 'create') {
              handleModalClose();
            } else {
              setLastOperation(null);
              fetchModules();
            }
          }}
        />
        {selectedRoleId && (
          <EditRoleModal
            isOpen={isEditingRole}
            onClose={() => {
              setIsEditingRole(false);
              setSelectedRoleId(null);
              if (lastOperation === 'edit') {
                handleModalClose();
              } else {
                setLastOperation(null);
                fetchModules();
              }
            }}
            roleId={selectedRoleId}
          />
        )}
      </div>

      <div className="sticky bottom-0 mt-3">
        <div className="w-full py-6">
          <div className="flex flex-col 2xl:flex-row 2xl:items-center 2xl:justify-end gap-4 w-full">
            <button
              onClick={() => {
                setIsAddingNewRole(true);
                setLastOperation('create');
              }}
              type="button"
              className="w-full 2xl:w-[150px] h-10 px-6 flex items-center justify-center space-x-2 border border-primary rounded-md text-[13px] font-semibold text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
            >
              <span>+ Add New Role</span>
            </button>
            <button
              type="button"
              onClick={(e) => handleSubmit(e, true)}
              className="w-full 2xl:w-[150px] h-10 px-6 flex items-center justify-center space-x-2 border border-primary rounded-md text-[15px] font-semibold text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
            >
              <span>Next</span>
              <FontAwesomeIcon icon={faArrowRight} className="ml-1" />
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {!fromSettings ? (
        <div className="w-screen h-screen overflow-auto flex justify-center items-start">
          <div className="w-11/12 p-4">
            <ButcherHeader isOnBoarding={true} />
            {renderContent}
          </div>
        </div>
      ) : (
        <OnBoardSettings renderContent={renderContent} />
      )}
    </>
  );
};